import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import {  RCCButton, RCCHookToast, RCCRefModalProps, RCCServicesApi} from "react-common-components";
import {  useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import RACI from "src/components/RACI";
import ModalSide from "src/components/Modal/styles";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import Tabs from "src/components/Tabs";
import extractSingleValueFromArrayOfObject from "src/utils/extractSingleValueFromArrayOfObject";
import { useAuth } from "src/context/Auth/context";
import { ITypeModal } from "src/components/Modal/interface";
import { checkSituationOccurrence, IComponentOccurrenceModal, IOccurrence, IRefModalAddOrChange, ISteps } from "../../interface";
import TabGeneral from "./modalTabs/tabGeneral";
import TabSteps from "./modalTabs/tabSteps";
import { addAndChangeSchema, addAndChangeSchemaSimplified } from "../../schemas";
import TabLinks from "./modalTabs/tablinks";
import FormSimplifiedFields from "../FormSimplifiedFields";


const modalAddOrChange : React.ForwardRefRenderFunction<IRefModalAddOrChange,IComponentOccurrenceModal> = ({
	onCallbackUpdate,
	loadGroupers
}, ref) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
	const modal = useRef<RCCRefModalProps>(null);
	const { user } = useAuth();
	const [formSimplified, setFormSimplified] = useState(true);
	const [ selectedOccurrence, setSelectedOccurrence] = useState<IOccurrence>({} as IOccurrence);
	const [ occurenceSteps, setOccurenceSteps ] = useState<ISteps[] | undefined>();
	const [isAdd, setIsAdd] = useState(false);
	const [isFinaly, setIsFinaly] = useState(false);
	const [isCopy, setIsCopy] = useState(false);

	const { handleSubmit, unregister, control, setError, register, reset, watch, formState: { errors }, clearErrors } = useForm();

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange
	}));

	const actionButtonAdd = () => {
		reset();
		setOccurenceSteps(undefined);
		setSelectedOccurrence({} as IOccurrence);
		setFormSimplified(true);
		clearErrors();
		setIsCopy(false);
		setIsAdd(true);
		modal.current?.openModal();
	};

	const actionButtonChange = (selectedOccurenceId: number, isCopyOccurence: boolean = false, isVisualize: boolean = false) => {
		reset();
		setOccurenceSteps(undefined);
		setIsCopy(isCopyOccurence);
		setFormSimplified(false);
		setIsAdd(false);
		clearErrors();
		queryOccurenceData(selectedOccurenceId, isVisualize);
	};

	const queryOccurenceData = async (id: number, isVisualize: boolean) => {
		setLoading(true);
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "Occurrences/Select",
				urlParameters: {
					id
				}
			});

			const statusFinishedOrCanceledOrDisapproved = data.ocb001Status === checkSituationOccurrence.FINISHED || data.ocb001Status === checkSituationOccurrence.CANCELED || data.ocb001Status === checkSituationOccurrence.DISAPPROVED;
			setIsFinaly(isVisualize || statusFinishedOrCanceledOrDisapproved);
			setSelectedOccurrence(data);
			setOccurenceSteps(data.octbb002);

			modal.current?.openModal();

			setLoading(false);
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao carregar a ocorrência"), addToast);
		}
	};
	
	const queryOccurenceSteps = async (categoryId : number) => {
		if(!categoryId)
			return; 

		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "Occurrences/ListStepsByCategory",
				urlParameters: {
					id: categoryId,
					dateTime: new Date(watch("ocb001Data")).toJSON()
				}
			});
			setOccurenceSteps(undefined);
			setOccurenceSteps(data);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar as etapas de ocorrência"), addToast);
		}
	};

	const onChangeCategoryOccurence = async (occurenceId : number | undefined) => {
		if(occurenceId)
			queryOccurenceSteps(occurenceId);
		else
			setOccurenceSteps(undefined);
	};

	const onSubmit = async (data: any) => {
		clearErrors();
		try{
			modal.current?.setLoadingButton();

			const dataChanged = data;
			dataChanged.ocb001NotificaResponsavel = data.ocb001NotificaResponsavel ? "S": "N";
			dataChanged.ocb001NotificaAutoridade = data.ocb001NotificaAutoridade ? "S": "N";
			dataChanged.ocb001Taa001Id = convertToInt(data.ocb001Taa001Id);
			dataChanged.ocb001Efa005Id = convertToInt(data.ocb001Efa005Id);
			dataChanged.ocb001Oca001Id = convertToInt(data.ocb001Oca001Id);
			dataChanged.ocb001Oca002Id = convertToInt(data.ocb001Oca002Id);
			dataChanged.ocb001Oca004Id = convertToInt(data.ocb001Oca004Id);
			dataChanged.ocb001Efa005Id4 = convertToInt(data.ocb001Efa005Id4);
			dataChanged.ocb001Efa005Id3 = convertToInt(data.ocb001Efa005Id3);

			if(data.octbb008 && data.octbb008.length > 0)
				dataChanged.octbb008 = dataChanged.octbb008.map((octbb008: any) => {
					if(typeof octbb008.ocb008Efa005Id === "object")
						return {
							...octbb008,
							ocb008Efa005Id: octbb008.ocb008Efa005Id.value,
							ocb008NotificaParticipante: octbb008.ocb008NotificaParticipante ? "S": "N"
						};
					if(typeof octbb008.ocb008Efa004Id === "object")
						return {
							...octbb008,
							ocb008Efa004Id: octbb008.ocb008Efa004Id.value,
							ocb008NotificaParticipante: octbb008.ocb008NotificaParticipante ? "S": "N"
						};
					return {
						...octbb008,
						ocb008NotificaParticipante: octbb008.ocb008NotificaParticipante ? "S": "N"
					};
				}).filter((e: any) => e.ocb008Efa005Id || e.ocb008Efa004Id);

			if(formSimplified && isAdd){
				await addAndChangeSchemaSimplified(translate).validate(data,{
					abortEarly: false
				});
			}else{
				await addAndChangeSchema(translate).validate(data,{
					abortEarly: false
				});
			}

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "Occurrences/Insert",
					body: {
						...data,
						ocb001Tags: data.ocb001Tags? extractSingleValueFromArrayOfObject("value", data.ocb001Tags): null,
						isSimplifiedForm: formSimplified
					}
				});
				
				if(loadGroupers)
					loadGroupers();
				
				addToast({
					color: "green",
					description: translate("Ocorrência adicionada com sucesso"),
					title: translate("Sucesso")
				});

			}else if(isCopy){

				await RCCServicesApi.postRequest({
					method: "Occurrences/Copy",
					body: {
						...data,
						ocb001Id: selectedOccurrence.ocb001Id,
						ocb001Tags: data.ocb001Tags? extractSingleValueFromArrayOfObject("value", data.ocb001Tags): null
					}
				});

				if(loadGroupers)
					loadGroupers();

				addToast({
					color: "green",
					description: translate("Ocorrência copiada com sucesso"),
					title: translate("Sucesso")
				});

			}else{

				await RCCServicesApi.postRequest({
					method: "Occurrences/Update",
					body: {
						...data,
						ocb001Id: selectedOccurrence.ocb001Id,
						ocb001Tags: data.ocb001Tags? extractSingleValueFromArrayOfObject("value", data.ocb001Tags): null
					}
				});

				if(onCallbackUpdate)
					onCallbackUpdate(selectedOccurrence.ocb001Id);

				addToast({
					color: "green",
					description: translate("Ocorrência alterada com sucesso"),
					title: translate("Sucesso")
				});
			}

			modal.current?.resetLoadingButton();
			modal.current?.closeModal();
		}catch(err: any){
			modal.current?.resetLoadingButton();

			if(getValidationErrors(err, setError)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
			}else{
				const textMessageError = err?.response?.data?.Message;
				if(textMessageError === "ocrAlreadyCanceled"){
					addToast({
						title: translate("Erro"),
						description: translate("A ocorrência não pode ser salva pois já se encontra concluída ou cancelada"),
						color: "yellow"
					});
				}else if(textMessageError === "endDatesGreaterThanStartDates"){
					addToast({
						title: translate("Erro"),
						description: translate("As datas finais das etapas devem ser maiores do que as datas iniciais"),
						color: "yellow"
					});
				}else if(textMessageError === "stepsDatesGreaterThanOccurrenceDate"){
					addToast({
						title: translate("Erro"),
						description: translate("As datas iniciais e finais das etapas devem ser maiores do que a data da própria ocorrência"),
						color: "yellow"
					});
				}else if(textMessageError === "canNoLongerReturnToThisSituation"){
					addToast({
						title: translate("Erro"),
						description: translate("Após a ocorrência deixar de ser nova ela não pode mais retornar para essa situação"),
						color: "yellow"
					});
				}else{
					standardErrorMessages(err, isAdd ? translate("Erro ao adicionar ocorrência") : translate("Erro ao alterar ocorrência"), addToast);	
				}
			}
		}
	};

	const convertToInt = (obj: any) => {
		if(!obj)
			return undefined;

		if(typeof obj === "object")
			return parseInt(obj.value, 10);

		if(typeof obj === "number")
			return obj;

		return parseInt(obj, 10);
	};

	const convertToString = (obj: any) => {
		if(!obj)
			return undefined;

		if(typeof obj === "object")
			return obj.value;

		if(typeof obj === "number")
			return obj.toString();

		return obj;
	};

	const titleModal = () => {
		if(isAdd) return translate("Adicionar Ocorrência");
		if(isCopy) return translate("Copiar Ocorrência");
		if(isFinaly) return translate("Visualizar Ocorrência");
		return translate("Alterar Ocorrência");
	};

	const iconModal = () => {
		if(isAdd) return "plus";
		if(isCopy) return "copy";
		if(isFinaly) return "eye";
		return "pencil";
	};

	return (
		<>
			<Modal
				type={isCopy ? ITypeModal.COPY : undefined}
				ref={modal} 
				header={{
					title: titleModal(),
					icon: {
						name: iconModal()
					},
					subtitle: !isAdd ? `${translate("Ocorrência")}: ${selectedOccurrence.fullTitle}` : ""
				}}
				styleInline={ModalSide}
				onSubmit={handleSubmit(onSubmit)}
				footer={{
					showSaveButton: !isFinaly,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Salvar"),
					cancelButtonText: translate("Cancelar"),
					childrenOfStandardFooter: <>{isAdd && (
						<RCCButton color="blue" size="sm"
							onClick={() => {
								const ocb001Codigo =  watch("ocb001Codigo");
								const ocb001Titulo = watch("ocb001Titulo");
								const ocb001Descricao = watch("ocb001Descricao");
								const ocb001Data = watch("ocb001Data");
								const ocb001Taa001Id = convertToString(watch("ocb001Taa001Id"));
								const ocb001Efa005Id = convertToString(watch("ocb001Efa005Id"));
								const ocb001Oca001Id = convertToString(watch("ocb001Oca001Id"));
								const ocb001Oca002Id = convertToString(watch("ocb001Oca002Id"));
								const ocb001Oca004Id = convertToString(watch("ocb001Oca004Id"));
								const ocb001Efa005Id4 = convertToString(watch("ocb001Efa005Id4"));
								const ocb001Efa005Id3 = convertToString(watch("ocb001Efa005Id3"));
								setSelectedOccurrence((prevState) => ({
									...prevState,
									ocb001Codigo,
									ocb001Titulo,
									ocb001Descricao,
									ocb001Data,
									ocb001Taa001Id,
									ocb001Efa005Id,
									ocb001Oca001Id,
									ocb001Oca004Id,
									ocb001Oca002Id,
									ocb001Efa005Id4,
									ocb001Efa005Id3
								}));
								setFormSimplified((prevState) => !prevState);
								if(occurenceSteps === undefined){
									queryOccurenceSteps(ocb001Oca004Id);
								}
							}}
						>
							{formSimplified ? translate("Ver cadastro completo") : translate("Ver cadastro simplificado")}
						</RCCButton>
					)}	
					</>
				}}
			>

				{formSimplified ? (
					<>
						<FormSimplifiedFields 
							control={control}
							error={errors}
							register={register}
							occurrence={selectedOccurrence}
						/>
					</>
				): (
					<Tabs 
						name="tabHistoric"
						tabInit={0}
						options={[
							{
								label: translate("Geral"),
								content: 
								<>
									<TabGeneral 
										control={control}
										watch={watch}
										disabled={isFinaly}
										error={errors}
										isAdd={isAdd}
										isCopy={isCopy}
										occurrence={selectedOccurrence}
										register={register}
										onChangeCategoryOccurence={onChangeCategoryOccurence}
									/>	
								</>
							},{
								label: translate("RACI"),
								content: (
									<RACI 
										control={control}
										disabled={isFinaly || (!isCopy && selectedOccurrence.ocb001Status === checkSituationOccurrence.OPENED)}
										register={register}
										unregister={unregister}
										clearErrors={clearErrors}
										error={errors}
										participants={{
											nameOfObject: "octbb008",
											nameOfID: "ocb008Id",
											nameOfCheckbox: "ocb008NotificaParticipante",
											nameOfSelectGroup: "ocb008Efa004Id",
											nameOfSelectGroupDescription: "ocb008Efa004Descricao",
											nameOfSelectUser: "ocb008Efa005Id",
											nameOfSelectUserDescription: "ocb008Efa005Nome",
											nameOfType: "ocb008Tipo",
											defaultValue: selectedOccurrence.octbb008
										}}
										responsible={{
											nameOfSelect: "ocb001Efa005Id4",
											nameOfCheckbox: "ocb001NotificaResponsavel",
											defaultValue: {
												select: selectedOccurrence.ocb001Efa005Id4 ? selectedOccurrence.ocb001Efa005Id4.toString() : user.efa005Id.toString(),
												checkbox: selectedOccurrence.ocb001NotificaResponsavel || "S"
											}
										}}
										accountable={{
											nameOfSelect: "ocb001Efa005Id3",
											nameOfCheckbox: "ocb001NotificaAutoridade",
											defaultValue: {
												select: selectedOccurrence.ocb001Efa005Id3 ? selectedOccurrence.ocb001Efa005Id3.toString() : user.efa005Id.toString(),
												checkbox: selectedOccurrence.ocb001NotificaAutoridade || "S"
											}
										}}
									/>
								)
							},{
								label: translate("Etapas"),
								content: (
									<>
										<TabSteps 
											control={control}
											disabled={isFinaly}
											error={errors}
											isAdd={isAdd}
											isCopy={isCopy}
											occurrenceSteps={occurenceSteps}
											register={register}
											clearErrors={clearErrors}
										/>
									</>
								)
							},
							{
								label: translate("Links"),
								content: (
									<>
										<TabLinks 
											control={control}
											disabled={isFinaly}
											error={errors}
											isAdd={isAdd}
											watch={watch}
											occurrenceLinks={selectedOccurrence.octbb005}
											register={register}
										/>
									</>
								)
							}
						]}
					/>
				)}

			</Modal>
		</>
	);
};

export default forwardRef(modalAddOrChange);
